
// charCodeAt for '0' and '9'
const CCAT_0 = 48;
const CCAT_9 = 57;

/**
 * is char code number
 * @param  {number} code ->  48='0' 57='9'
 * @return {boolean}
 */
const isNumberCode = (code: number) => code >= CCAT_0 && code <= CCAT_9


/**
 * If value is number-ish
 * @param  {unknown} value
 * @return {value is number}
 */
export const isNumeric = (value: unknown): value is number => {
  const val = typeof value === 'string' ? value.replace(/,/g, '') : value;
  return !Number.isNaN(Number.parseFloat(val as string))
    && isFinite(val as number)
    && Object.prototype.toString.call(val).toLowerCase() !== '[object array]';
};


/**
 * A "better" or more natural way of sorting, especially filenames
 * @param  {string | number} a
 * @param  {string | number} b
 * @return {number}
 * @example
    const files = ['y1.doc', 'y10.doc', 'y17.doc', 'y2.doc', 'y23.doc', 'y3.doc']
    files.sort()            -> y1.doc, y10.doc, y17.doc, y2.doc, y23.doc, y3.doc
    files.sort(naturalSort) -> y1.doc, y2.doc, y3.doc, y10.doc, y17.doc, y23.doc
 */
export const naturalSort = (a: string | number, b: string | number) => {
  if (isNumeric(a) && isNumeric(b)) { return Number(a) - Number(b); }
  if (typeof a !== 'string' || typeof b !== 'string') {
    console.error('[naturalSort] expected string, not: (' + typeof a + ', ' + typeof b + ')');
    return 0;
  }
  const lengthA = a.length;
  const lengthB = b.length;
  let ia = 0;
  let ib = 0;
  let res = 0;
  // ignore casing
  a = a.toLowerCase();
  b = b.toLowerCase();
  while (ia < lengthA && ib < lengthB) {
    let cca = a.charCodeAt(ia);
    let ccb = b.charCodeAt(ib);
    if (isNumberCode(cca)) {
      if (!isNumberCode(ccb)) { return cca - ccb; }
      // number start
      let nsa = ia;
      let nsb = ib;
      while (cca === CCAT_0 && ++nsa < lengthA) { cca = a.charCodeAt(nsa); }
      while (ccb === CCAT_0 && ++nsb < lengthB) { ccb = b.charCodeAt(nsb); }
      if (nsa !== nsb && res === 0) { res = nsa - nsb; }

      // number end
      let nea = nsa;
      let neb = nsb;
      while (nea < lengthA && isNumberCode(a.charCodeAt(nea))) { ++nea; }
      while (neb < lengthB && isNumberCode(b.charCodeAt(neb))) { ++neb; }

      // numA length - numB length
      let diff = nea - nsa - neb + nsb;
      if (diff !== 0) { return diff; }

      while (nsa < nea) {
        diff = a.charCodeAt(nsa++) - b.charCodeAt(nsb++);
        if (diff !== 0) { return diff; }
      }
      ia = nea;
      ib = neb;
      continue;
    }
    if (cca !== ccb) { return cca - ccb; }
    ++ia;
    ++ib;
  }
  // 'b' is a substring of 'a'
  if (ia < lengthA) { return 1; }
  // 'a' is a substring of 'b'
  if (ib < lengthB) { return -1; }
  return res;
};
